import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Youtube, Facebook, Instagram, MessageSquareQuote } from 'lucide-react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import VideoCarousel from '../components/VideoCarousel';
import TextCarousel from '../components/TextCarousel';

/**
 * LandingHero — Iter-51.55.
 *
 * The pre-login door. Redesigned to fit the entire experience on a
 * single 375×700 mobile viewport without scrolling:
 *   ┌ 56px  logo + wordmark + tagline
 *   ├ 100px SIGN UP button + "Log in" secondary link
 *   ├ 190px video testimonial (16:9 poster + dots)
 *   ├ 180px text/screenshot testimonial + dots
 *   └ 16px  © footer (absolute-positioned)
 *
 * Existing users tap the small "Already have an account? Log in"
 * link and land on `/login` (the email/password + Google form,
 * unchanged from before).
 */
export default function LandingHero() {
  const { user, authLoading } = useApp();
  const [videos, setVideos] = useState([]);
  const [texts, setTexts] = useState([]);
  // Iter-52.7 — social icons live ONLY here on the logged-out landing
  // page, never inside any authenticated surface. Rule: no path from
  // the child-facing app to YouTube/Facebook/Instagram, ever. This is
  // parent-facing surface, so it's fine.
  const [socialLinks, setSocialLinks] = useState({ youtube: '', facebook: '', instagram: '' });

  useEffect(() => {
    let cancelled = false;
    api.get('/config/testimonials')
      .then(({ data }) => {
        if (cancelled) return;
        setVideos(data?.videos || []);
        setTexts(data?.texts || []);
      })
      .catch(() => { /* silent — carousels just hide if empty */ });
    api.get('/config/social-links')
      .then(({ data }) => { if (!cancelled) setSocialLinks(data || {}); })
      .catch(() => { /* silent — the strip self-hides when all URLs are empty */ });
    return () => { cancelled = true; };
  }, []);

  if (authLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/>
    </div>
  );
  if (user) return <Navigate to="/profiles" replace/>;

  return (
    <div
      data-testid="landing-hero"
      className="min-h-screen w-full flex flex-col items-center px-5 pt-4 pb-8 relative overflow-hidden"
    >
      {/* Decorative blobs — kept smaller than /login version to leave
          room for carousels without visual clutter. */}
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#F4B740] blur-3xl opacity-60 pointer-events-none"/>
      <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#D97398] blur-3xl opacity-60 pointer-events-none"/>

      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        {/* Row 1 — compact logo + wordmark + tagline. Total ~90px. */}
        <img
          data-testid="landing-logo"
          src="/assets/readbuddy-logo.png"
          alt="ReadBuddy"
          className="w-32 mb-1 select-none pointer-events-none"
        />
        <p
          data-testid="landing-tagline"
          className="text-sm font-bold text-[#2B2140] text-center leading-snug"
        >
          Making reading fun for the students
        </p>

        {/* Row 2 — Primary CTA (SIGN UP) + secondary login link. */}
        <Link
          data-testid="landing-signup-btn"
          to="/register"
          className="pushable w-full mt-4 bg-[#FF6B1A] text-white text-lg font-extrabold py-3 rounded-full inline-flex items-center justify-center gap-2 shadow-lg shadow-[#FF6B1A]/25"
        >
          Sign Up <ArrowRight size={18} strokeWidth={3}/>
        </Link>
        <p className="text-xs font-semibold text-[#2B2140]/70 mt-2">
          Already have an account?{' '}
          <Link data-testid="landing-login-link" to="/login" className="text-[#5B8DEF] font-extrabold underline underline-offset-2">
            Log in
          </Link>
        </p>

        {/* Row 3 — Video testimonial carousel. Renders an empty-state
            placeholder frame when there are no items yet so the
            layout structure stays visible and parents don't wonder
            if the page is broken. */}
        {/* Iter-51.87 — parent-requested (this iter): remove ALL text
            from both carousel sections — headings, subtitles, and
            "coming soon" copy. The empty-state boxes now show only
            the section icon. Rationale: parent will drop general
            promo content into these slots, and static labels would
            be misleading. */}
        <div data-testid="landing-video-section" className="w-full mt-5">
          {videos.length > 0 ? (
            <VideoCarousel items={videos}/>
          ) : (
            <div
              data-testid="landing-video-empty"
              className="w-full rounded-2xl bg-white/70 backdrop-blur border-2 border-dashed border-[#2B2140]/20 aspect-video flex items-center justify-center"
            >
              <div className="w-11 h-11 rounded-full bg-[#FF6B1A]/15 text-[#FF6B1A] flex items-center justify-center">
                <Youtube size={20} strokeWidth={2.5}/>
              </div>
            </div>
          )}
        </div>

        {/* Row 4 — Text/screenshot carousel. Same empty-state approach
            with a MessageSquareQuote icon to visually differentiate
            from the video block above. */}
        <div data-testid="landing-text-section" className="w-full mt-4">
          {texts.length > 0 ? (
            <TextCarousel items={texts}/>
          ) : (
            <div
              data-testid="landing-text-empty"
              className="w-full rounded-2xl bg-white/70 backdrop-blur border-2 border-dashed border-[#2B2140]/20 py-6 flex items-center justify-center"
            >
              <div className="w-10 h-10 rounded-full bg-[#5B8DEF]/15 text-[#5B8DEF] flex items-center justify-center">
                <MessageSquareQuote size={18} strokeWidth={2.5}/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Iter-52.7 — Social icons: YouTube / Facebook / Instagram.
          Only on the logged-out landing so children never see a path
          from the app to social media. Admin manages the three URLs
          from the debug drawer's Social-media-links card. Each icon
          hides if its URL is empty; the whole strip hides if none
          are set. */}
      <LandingSocialLinks links={socialLinks}/>

      {/* Compact bottom brand + copyright — moved here from the
          top so it stops eating the primary hero's vertical
          budget. */}
      <p data-testid="landing-copyright" className="absolute bottom-3 inset-x-0 z-10 text-center text-[10px] font-semibold text-[#2B2140]/55">
        © 2026 Vownex Innovations. All rights reserved.
      </p>
    </div>
  );
}

/**
 * LandingSocialLinks — iter-52.9.
 *
 * The three social handles pinned as a VERTICAL rail on the right
 * edge of the landing page (moved from the horizontal bottom row
 * in iter-52.7 because the row was overlapping the testimonial
 * carousel copy at the bottom of the viewport).
 *
 * Placement: `fixed`, top-half of the viewport, right edge. The
 * strip self-hides when no URL is set (fresh install, or admin
 * cleared all three). Icons have a soft white/backdrop chip so
 * they read well against any hero colour.
 */
function LandingSocialLinks({ links }) {
  const entries = [
    { key: 'youtube',   Icon: Youtube,   bg: 'bg-[#FF0000]', label: 'YouTube'   },
    { key: 'facebook',  Icon: Facebook,  bg: 'bg-[#1877F2]', label: 'Facebook'  },
    { key: 'instagram', Icon: Instagram, bg: 'bg-[#E1306C]', label: 'Instagram' },
  ].filter(e => !!(links || {})[e.key]);
  if (entries.length === 0) return null;
  return (
    <div data-testid="landing-social-links"
      className="fixed right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-2.5">
      {entries.map(({ key, Icon, bg, label }) => (
        <a key={key}
          data-testid={`landing-social-${key}`}
          href={links[key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`ReadBuddy on ${label}`}
          className={`w-9 h-9 rounded-xl ${bg} text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md ring-2 ring-white/60`}
        >
          <Icon size={17} strokeWidth={2.6}/>
        </a>
      ))}
    </div>
  );
}
